<script>
import { mapStores } from "pinia";
import { RouterLink } from "vue-router";

import Dictionaries, { MONOLINGUAL } from "@/common/dictionaries";
import { isIframe } from "@/common/is-iframe";
import { LEVEL_BEGINNER } from "@/common/level-types";
import { addPackageAndLangParams } from "@/common/route-path-helpers";
import ArabicArticleContent from "@/components/article/arabic/ArticleContent.vue";
import GenericArticleFooter from "@/components/article/ArticleFooter.vue";
import CentlexArticleContent from "@/components/article/centlex/ArticleContent.vue";
import DictsysArticleContent from "@/components/article/dictsys/ArticleContent.vue";
import DictsysInflectionScheme from "@/components/article/dictsys/InflectionScheme.vue";
import DirectionSwitchInfoBox from "@/components/article/DirectionSwitchInfoBox.vue";
import DsnArticleContent from "@/components/article/dsn/ArticleContent.vue";
import GreenlandicArticleContent from "@/components/article/greenlandic/GreenlandicArticleContent.vue";
import GyldendalArticleContent from "@/components/article/gyldendal/ArticleContent.vue";
import GyldendalArticleFooter from "@/components/article/gyldendal/ArticleFooter.vue";
import GyldendalInflectionScheme from "@/components/article/gyldendal/InflectionScheme.vue";
import PolAdvancedDanishEnglishArticleContent from "@/components/article/politiken/advancedDanishEnglish/DanishArticleContent.vue";
import PolAdvancedEnglishDanishArticleContent from "@/components/article/politiken/advancedDanishEnglish/EnglishArticleContent.vue";
import PolChildrensDanishArticleContent from "@/components/article/politiken/childrensDanish/ArticleContent.vue";
import PolChildrensDanishEnglishArticleContent from "@/components/article/politiken/childrensDanishEnglish/ArticleContent.vue";
import PolCurrentDanishArticleContent from "@/components/article/politiken/currentDanish/ArticleContent.vue";
import PolDanishEnglishArticleContent from "@/components/article/politiken/danishEnglish/ArticleContent.vue";
import PolForeignDanishArticleContent from "@/components/article/politiken/foreignDanish/ArticleContent.vue";
import PolSchoolDanishArticleContent from "@/components/article/politiken/schoolDanish/ArticleContent.vue";
import PolSimplifiedCurrentDanishArticleContent from "@/components/article/politiken/simplifiedCurrentDanish/ArticleContent.vue";
import PolThesaurusDanishArticleContent from "@/components/article/politiken/thesaurusDanish/ArticleContent.vue";
import RandomHouseArticleContent from "@/components/article/random-house/ArticleContent.vue";
import DanishRussianArticleContent from "@/components/article/russian/DanishArticleContent.vue";
import RussianDanishArticleContent from "@/components/article/russian/RussianArticleContent.vue";
import SearchLinkList from "@/components/article/SearchLinkList.vue";
import DictionaryControls from "@/components/DictionaryControls.vue";
import EasterEggManager from "@/components/EasterEggManager.vue";
import ImageResponsive from "@/components/ImageResponsive.vue";
import SearchBar from "@/components/searchBar/SearchBar.vue";
import { MatomoHelper } from "@/helpers";
import { isDictsysFormat, isGyldendalFormat } from "@/helpers/article-format.helper";
import { i18n } from "@/i18n";
import {
  useElementRegistryStore,
  useInflectionSchemeStore,
  useLanguageLevelStore,
  useSearchStore,
  useSettingsStore
} from "@/stores";
import { MatomoEventAction, MatomoEventCategory } from "@/types";

export default {
  name: "ArticleView",
  components: {
    CentlexArticleContent,
    RandomHouseArticleContent,
    PolAdvancedDanishEnglishArticleContent,
    PolAdvancedEnglishDanishArticleContent,
    PolForeignDanishArticleContent,
    PolThesaurusDanishArticleContent,
    PolSimplifiedCurrentDanishArticleContent,
    PolDanishEnglishArticleContent,
    PolChildrensDanishEnglishArticleContent,
    PolCurrentDanishArticleContent,
    PolChildrensDanishArticleContent,
    PolSchoolDanishArticleContent,
    DanishRussianArticleContent,
    RussianDanishArticleContent,
    DictionaryControls,
    ArabicArticleContent,
    DictsysArticleContent,
    DictsysInflectionScheme,
    DirectionSwitchInfoBox,
    GreenlandicArticleContent,
    GyldendalArticleContent,
    GyldendalInflectionScheme,
    GyldendalArticleFooter,
    DsnArticleContent,
    EasterEggManager,
    GenericArticleFooter,
    SearchBar,
    SearchLinkList,
    ImageResponsive,
    RouterLink
  },
  beforeRouteLeave(to, from, next) {
    this.resetSearchResult();

    next();
  },
  props: {
    inflectionSchemeId: {
      type: String,
      default: undefined
    },
    searchWord: {
      type: String,
      required: true
    },
    searchDictionary: {
      type: String,
      default: undefined
    },
    searchDirection: {
      type: String,
      required: true
    },
    searchDirectionForced: {
      type: Boolean,
      default: false
    },
    searchTab: {
      type: String,
      default: undefined
    }
  },
  data() {
    return {
      controlId: "dictionary-controls",
      isAdjustedSearch: false,
      oldSearchDirection: undefined
    };
  },
  computed: {
    ...mapStores(
      useElementRegistryStore,
      useInflectionSchemeStore,
      useSearchStore,
      useLanguageLevelStore,
      useSettingsStore
    ),

    level() {
      return this["language-levelStore"].activeLanguageLevel;
    },
    searchDictionaryId() {
      if (
        this.searchDictionary === undefined ||
        Dictionaries.getTypeFromDictionaryId(this.searchDictionary) !== MONOLINGUAL
      ) {
        return undefined;
      }

      return this.searchDictionary;
    },

    searchResult() {
      return this.searchStore.searchResult;
    },

    tabResult() {
      const tabResult = [];

      this.searchResult.forEach((result) => {
        if (
          result.types.includes(this.searchStore.articleTab) ||
          this.searchStore.articleTab === undefined
        ) {
          tabResult.push(result);
        }
      });

      return tabResult;
    },

    isWordCompletionOpen() {
      return this.searchStore.isWordCompletionOpen;
    },
    isSearchExact() {
      return this.searchStore.searchState === "exactResult";
    },
    isSearchUnauthorized() {
      return this.searchStore.searchState === "unauthorized";
    },
    isSearchEmpty() {
      return this.searchStore.searchState === "emptyResult";
    },
    isSearchFuzzy() {
      return this.searchStore.searchState === "fuzzyResult";
    },
    isSearching() {
      return this.searchStore.searchState === "loading";
    },

    searchResultFuzzyWords() {
      return [...new Set(this.searchResult.map((result) => result.search))];
    },
    showImageByDefault() {
      return this.level === LEVEL_BEGINNER;
    },
    searchIndex() {
      return `${this.searchWord}_${this.searchDirectionForced}_${this.searchDictionaryId}_${this.searchTab}`;
    },
    directionSwitchInfoBoxEnabled() {
      return Dictionaries.getDirectionsSupportByDirection(this.searchDirection);
    },
    showArticleTools() {
      return !isIframe();
    },
    ordbogenArticleTranslationsExpandedDefault() {
      return this.settingsStore.userPreferences.ordbogenArticleTranslationsExpandedDefault;
    }
  },
  watch: {
    inflectionSchemeId: function (newVal) {
      if (newVal === undefined) {
        this.closeInflectionScheme();
      } else {
        this.openInflectionScheme(newVal);
      }
    },
    async searchDirection(value, old) {
      if (this.searchDirectionForced) {
        if (this.searchDirection) {
          this.searchStore.searchDirection = this.searchDirection;
        }

        if (this.searchWord) {
          await this.searchWithDirection();
        }

        return;
      }

      // If the direction is changed, then it means
      // we automatically set another search direction.
      // This will never happen when using forced/strict direction search
      this.isAdjustedSearch = true;
      this.oldSearchDirection = old;
    },
    searchTab() {
      // When they switch tab, we no longer consider the search adjusted,
      // as they just performed a new search.
      this.isAdjustedSearch = false;
    },
    searchIndex: {
      async handler() {
        // Will reset the search dictionary id if moving from monolingual to bilingual
        this.searchStore.searchDictionaryId = this.searchDictionary;

        if (this.searchDirection) {
          this.searchStore.searchDirection = this.searchDirection;
        }

        if (this.searchWord) {
          this.isAdjustedSearch = false;
          this.searchStore.searchWord = this.searchWord;

          if (this.searchDirectionForced) {
            await this.searchWithDirection();
          } else {
            await this.search();
          }

          if (this.inflectionSchemeId) {
            this.openInflectionScheme(this.inflectionSchemeId);
          }
        }
      },
      immediate: true
    },
    isSearchFuzzy: function (isFuzzy) {
      if (!isFuzzy) {
        return;
      }

      MatomoHelper.sendEvent(
        MatomoEventCategory.Search,
        MatomoEventAction.FuzzyResultPageShown,
        "Fuzzy result page shown"
      );
    }
  },
  methods: {
    addPackageAndLangParams,

    openInflectionScheme(inflectionSchemeId) {
      this["inflection-schemeStore"].openInflectionScheme(inflectionSchemeId);
    },
    closeInflectionScheme() {
      this["inflection-schemeStore"].closeInflectionScheme();
    },

    resetSearchResult() {
      this.searchStore.resetSearchResult();
    },

    async searchWithDirection() {
      const searchRequestMethods =
        Dictionaries.getSearchRequestMethodsFromLanguageGroupAndDirectionAndLevel(
          this.settingsStore.activeLanguageGroup ?? "",
          this.searchDirection,
          this.level
        );

      searchRequestMethods.filterByDirectionAndSourceLanguage(this.searchDirection);

      await this.search(searchRequestMethods);
    },

    async search(methodIds = undefined) {
      await this.searchStore.search(methodIds);

      if (this.$refs.articles) {
        this["element-registryStore"].registerArticles(this.$refs.articles);
      }
    },

    isDictsysFormat(methodId) {
      return isDictsysFormat(methodId);
    },

    isGyldendalFormat(methodId) {
      return isGyldendalFormat(methodId);
    },

    isFirstGyldendalFormat(methodId) {
      return (
        this.tabResult
          .filter((result) => isGyldendalFormat(result.methodId))
          .map((result) => result.methodId)
          .indexOf(methodId) === 0
      );
    },

    showPublisher(dictionaryIndex) {
      if (this.isSearching || this.isSearchEmpty || this.isSearchUnauthorized) {
        return false;
      }

      if (this.isSearchFuzzy) {
        return dictionaryIndex === this.tabResult.length - 1;
      }

      return true;
    },

    showGenericArticleFooter(dictionary) {
      return !isGyldendalFormat(dictionary.methodId);
    },

    showGyldendalArticleFooter(dictionary) {
      return isGyldendalFormat(dictionary.methodId);
    },

    getDictionaryNameByMethodId(methodId) {
      const dictionaryId = Dictionaries.getDictionaryIdByMethodId(methodId);

      if (!dictionaryId) {
        return "";
      }

      // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
      return i18n.global.t(`article.dictionaries.${dictionaryId}`);
    },

    trackSpellingRulesClick() {
      MatomoHelper.sendEvent(
        MatomoEventCategory.AbcWorkshop,
        MatomoEventAction.ClickSpellingRules,
        "Clicked Spelling rules article"
      );
    }
  }
};
</script>

<template>
  <main class="container article-view px-0 mb-5">
    <div
      class="article-view__word-completion-backdrop"
      :class="{ 'article-view__word-completion-backdrop--active': isWordCompletionOpen }"
    ></div>
    <SearchBar class="article-view__element" :scroll-target="controlId" />

    <Transition name="fade">
      <div v-if="isSearching === false">
        <DirectionSwitchInfoBox
          v-if="directionSwitchInfoBoxEnabled && isAdjustedSearch"
          class="article-view__element"
          :old-search-direction="oldSearchDirection"
          :search-direction="searchDirection"
        />
        <DictionaryControls
          v-if="isSearchExact"
          :id="controlId"
          class="article-view__element"
          :search-result="searchResult"
          :search-tab="searchTab"
        />
        <div v-if="isSearchUnauthorized" class="article-view__no-results-container">
          <h3 class="mb-2 text-center">{{ $t("article.unuathorized.heading") }}</h3>
          <p class="text-center">{{ $t("article.unuathorized.subtext") }}</p>
        </div>
        <div v-if="isSearchEmpty" class="article-view__no-results-container">
          <ImageResponsive
            src="/assets/img/boy-with-looking-glass@1x.png"
            class="mb-5"
            presentational
          />
          <h3 class="mb-2 text-center">{{ $t("article.noResults.heading") }}</h3>
          <p class="text-center">{{ $t("article.noResults.subtext") }}</p>
        </div>
        <div
          v-if="isSearchFuzzy && searchResultFuzzyWords.length > 0"
          class="row no-gutters article-view__element"
        >
          <div class="col">
            <SearchLinkList
              class="article-search-link_list"
              :search-dictionary="searchDictionary"
              :search-direction="searchDirection"
              :search-words="searchResultFuzzyWords"
            />
          </div>
        </div>
        <div ref="articles">
          <div v-for="(result, resultIndex) in tabResult" :key="resultIndex">
            <div
              class="row no-gutters"
              :class="{
                'article-view__element': !isSearchFuzzy || resultIndex === 0,
                'mt-5': !isSearchFuzzy && resultIndex > 0
              }"
            >
              <div v-if="isDictsysFormat(result.methodId)" class="col">
                <DictsysInflectionScheme />
                <DictsysArticleContent
                  v-for="(article, articleIndex) in result.articles"
                  :key="`${resultIndex}${articleIndex}${searchIndex}`"
                  :search="result.search"
                  :article="article"
                  :is-collapsible="isSearchFuzzy"
                  :translations-expanded-default="ordbogenArticleTranslationsExpandedDefault"
                  :class="{ 'article-content--chained': isSearchFuzzy }"
                />
              </div>
              <div v-else-if="isGyldendalFormat(result.methodId)" class="col">
                <GyldendalInflectionScheme v-if="resultIndex === 0" />
                <GyldendalArticleContent
                  v-for="(article, articleIndex) in result.articles"
                  :key="`${resultIndex}${articleIndex}${searchIndex}`"
                  :article="article"
                  :is-fuzzy="isSearchFuzzy"
                  :show-image-by-default="showImageByDefault"
                  :has-reading-lines="true"
                  :class="{
                    'article-content--unchained': isSearchFuzzy && !showPublisher(resultIndex)
                  }"
                />
              </div>
              <div
                v-else-if="['ordbogen-daar', 'ordbogen-arda'].includes(result.methodId)"
                class="col"
              >
                <div :class="{ 'article-view__element': resultIndex > 0 }">
                  <ArabicArticleContent
                    :key="`${resultIndex}${searchIndex}`"
                    :articles="result.articles"
                  />
                </div>
              </div>
              <div v-else-if="result.methodId === 'dsn-spelling-dada'" class="col">
                <div v-if="showArticleTools && resultIndex === 0" class="article-tools">
                  <RouterLink
                    class="article-tools__link"
                    :to="addPackageAndLangParams({ name: 'workshop.dsn-spelling-rules' })"
                    @click="trackSpellingRulesClick()"
                  >
                    {{ $t("article.tools.dsn.viewSpellingRules") }}
                  </RouterLink>
                </div>
                <div class="article-view__element">
                  <DsnArticleContent
                    :key="`${resultIndex}${searchIndex}`"
                    :articles="result.articles"
                  />
                </div>
              </div>
              <div v-else-if="result.methodId === 'pol-foreign-dada'" class="col">
                <PolForeignDanishArticleContent
                  v-for="(article, articleIndex) in result.articles"
                  :key="`${resultIndex}${articleIndex}${searchIndex}`"
                  :article="article"
                  :is-collapsible="isSearchFuzzy"
                  :class="{ 'article-content--chained': isSearchFuzzy }"
                />
              </div>
              <div v-else-if="result.methodId === 'pol-thesaurus-dada'" class="col">
                <PolThesaurusDanishArticleContent
                  v-for="(article, articleIndex) in result.articles"
                  :key="`${resultIndex}${articleIndex}${searchIndex}`"
                  :article="article"
                  :is-collapsible="isSearchFuzzy"
                  :class="{ 'article-content--chained': isSearchFuzzy }"
                />
              </div>
              <div v-else-if="result.methodId === 'pol-simp-current-dada'" class="col">
                <PolSimplifiedCurrentDanishArticleContent
                  v-for="(article, articleIndex) in result.articles"
                  :key="`${resultIndex}${articleIndex}${searchIndex}`"
                  :article="article"
                  :is-collapsible="isSearchFuzzy"
                  :class="{ 'article-content--chained': isSearchFuzzy }"
                />
              </div>
              <div v-else-if="result.methodId === 'pol-current-dada'" class="col">
                <PolCurrentDanishArticleContent
                  v-for="(article, articleIndex) in result.articles"
                  :key="`${resultIndex}${articleIndex}${searchIndex}`"
                  :article="article"
                  :is-collapsible="isSearchFuzzy"
                  :class="{ 'article-content--chained': isSearchFuzzy }"
                />
              </div>
              <div v-else-if="result.methodId === 'pol-school-dada'" class="col">
                <PolSchoolDanishArticleContent
                  v-for="(article, articleIndex) in result.articles"
                  :key="`${resultIndex}${articleIndex}${searchIndex}`"
                  :article="article"
                  :is-collapsible="isSearchFuzzy"
                  :class="{ 'article-content--chained': isSearchFuzzy }"
                />
              </div>
              <div v-else-if="result.methodId === 'pol-childrens-dada'" class="col">
                <PolChildrensDanishArticleContent
                  v-for="(article, articleIndex) in result.articles"
                  :key="`${resultIndex}${articleIndex}${searchIndex}`"
                  :article="article"
                  :is-collapsible="isSearchFuzzy"
                  :class="{ 'article-content--chained': isSearchFuzzy }"
                />
              </div>
              <div v-if="result.methodId === 'pol-advanced-daen'" class="col">
                <PolAdvancedDanishEnglishArticleContent
                  v-for="(article, articleIndex) in result.articles"
                  :key="`${resultIndex}${articleIndex}${searchIndex}`"
                  :article="article"
                  :is-collapsible="isSearchFuzzy"
                  :class="{ 'article-content--chained': isSearchFuzzy }"
                />
              </div>
              <div v-if="result.methodId === 'pol-advanced-enda'" class="col">
                <PolAdvancedEnglishDanishArticleContent
                  v-for="(article, articleIndex) in result.articles"
                  :key="`${resultIndex}${articleIndex}${searchIndex}`"
                  :article="article"
                  :is-collapsible="isSearchFuzzy"
                  :class="{ 'article-content--chained': isSearchFuzzy }"
                />
              </div>
              <div v-else-if="['pol-daen', 'pol-enda'].includes(result.methodId)" class="col">
                <PolDanishEnglishArticleContent
                  v-for="(article, articleIndex) in result.articles"
                  :key="`${resultIndex}${articleIndex}${searchIndex}`"
                  :article="article"
                  :is-collapsible="isSearchFuzzy"
                  :class="{ 'article-content--chained': isSearchFuzzy }"
                />
              </div>
              <div
                v-else-if="['pol-childrens-daen', 'pol-childrens-enda'].includes(result.methodId)"
                class="col"
              >
                <PolChildrensDanishEnglishArticleContent
                  v-for="(article, articleIndex) in result.articles"
                  :key="`${resultIndex}${articleIndex}${searchIndex}`"
                  :article="article"
                  :is-collapsible="isSearchFuzzy"
                  :class="{ 'article-content--chained': isSearchFuzzy }"
                />
              </div>
              <div v-else-if="result.methodId === 'gyldendal-red-daru'" class="col">
                <DanishRussianArticleContent
                  v-for="(article, articleIndex) in result.articles"
                  :key="`${resultIndex}${articleIndex}${searchIndex}`"
                  :article="article"
                  :is-collapsible="isSearchFuzzy"
                />
              </div>
              <div v-else-if="result.methodId === 'gyldendal-red-ruda'" class="col">
                <RussianDanishArticleContent
                  v-for="(article, articleIndex) in result.articles"
                  :key="`${resultIndex}${articleIndex}${searchIndex}`"
                  :article="article"
                  :is-collapsible="isSearchFuzzy"
                />
              </div>
              <div v-else-if="result.methodId === 'random-enen'" class="col">
                <RandomHouseArticleContent
                  v-for="(article, articleIndex) in result.articles"
                  :key="`${resultIndex}${articleIndex}${searchIndex}`"
                  :article="article"
                  :is-collapsible="isSearchFuzzy"
                />
              </div>
              <div v-else-if="result.methodId === 'ordbogen-klkl'" class="col">
                <GreenlandicArticleContent
                  v-for="(article, articleIndex) in result.articles"
                  :key="`${resultIndex}${articleIndex}${searchIndex}`"
                  :article="article"
                  :is-collapsible="isSearchFuzzy"
                />
              </div>
              <div v-else-if="result.methodId === 'cent-dada'" class="col">
                <CentlexArticleContent
                  v-for="(article, articleIndex) in result.articles"
                  :key="`${resultIndex}${articleIndex}${searchIndex}`"
                  :article="article"
                  :is-collapsible="isSearchFuzzy"
                />
              </div>
            </div>
            <div v-if="showPublisher(resultIndex)" class="row no-gutters">
              <div class="col">
                <GyldendalArticleFooter
                  v-if="showGyldendalArticleFooter(result)"
                  :dictionary-name="getDictionaryNameByMethodId(result.methodId)"
                />
                <GenericArticleFooter
                  v-else-if="showGenericArticleFooter(result)"
                  :dictionary-name="getDictionaryNameByMethodId(result.methodId)"
                />
              </div>
            </div>
          </div>
        </div>
        <EasterEggManager aria-hidden="true" />
      </div>
    </Transition>
  </main>
</template>

<style lang="less" scoped>
.fade {
  &-enter-from,
  &-leave-to {
    opacity: 0;
  }

  &-enter-active {
    transition: opacity 150ms ease-in;

    @media (prefers-reduced-motion: reduce) {
      transition: opacity 35ms;
    }
  }

  &-leave-active {
    transition: none;
  }
}
</style>
